import React, { useEffect, useMemo, useState } from 'react';
import { useDreAccess } from '../hooks/useDreAccess';
import DreIndicadores from '../components/DreIndicadores';
import DrePlanoAcao from '../components/DrePlanoAcao';
import DreUnidades from '../components/DreUnidades';
import { nomeMes } from '../utils/dreFormat';
import { IconChart, IconAlert } from '../components/Icons';
import './Dre.css';

// Período inicial = mês anterior ao atual (mês normalmente já fechado).
function periodoInicial() {
  const hoje = new Date();
  const anterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return { ano: anterior.getFullYear(), mes: anterior.getMonth() + 1 };
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Falha ao salvar');
  return data;
}

function Dre() {
  const { isGerente, escopo, chave, regiao, loading: accessLoading } = useDreAccess();

  const inicial = useMemo(periodoInicial, []);
  const [ano, setAno] = useState(inicial.ano);
  const [mes, setMes] = useState(inicial.mes);

  const [indicadores, setIndicadores] = useState([]);
  const [justificativas, setJustificativas] = useState({});
  const [planoAcao, setPlanoAcao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const anosDisponiveis = useMemo(() => {
    const atual = inicial.mes === 12 ? inicial.ano + 1 : inicial.ano; // ano do mês corrente
    return [atual, atual - 1, atual - 2, atual - 3];
  }, [inicial]);

  // Carrega resultados + justificativas/plano do escopo próprio ao trocar período.
  useEffect(() => {
    if (accessLoading || !isGerente) return;

    let cancelado = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetch(`/api/getDreResultados?ano=${ano}&mes=${mes}`).then(r => r.json()),
      fetch(`/api/getDreJustificativas?ano=${ano}&mes=${mes}`).then(r => r.json())
    ])
      .then(([resResultados, resJust]) => {
        if (cancelado) return;
        if (resResultados.error) { setError(resResultados.error); return; }
        if (resJust.error) { setError(resJust.error); return; }
        setIndicadores(resResultados.indicadores || []);
        setJustificativas(resJust.justificativas || {});
        setPlanoAcao(resJust.planoAcao || null);
      })
      .catch(() => { if (!cancelado) setError('Erro ao carregar dados do DRE'); })
      .finally(() => { if (!cancelado) setLoading(false); });

    return () => { cancelado = true; };
  }, [ano, mes, accessLoading, isGerente]);

  const handleSaveJustificativa = async (tipoIndicador, descricao) => {
    const data = await postJson('/api/saveDreJustificativa', { ano, mes, tipoIndicador, descricao });
    setJustificativas(prev => ({ ...prev, [tipoIndicador]: data.justificativa }));
  };

  const handleSavePlano = async (descricao) => {
    const data = await postJson('/api/saveDrePlanoAcao', { ano, mes, descricao });
    setPlanoAcao(data.planoAcao);
  };

  if (accessLoading) {
    return <div className="dre-container"><p className="loading-text">Verificando acesso...</p></div>;
  }

  const subtitulo = escopo === 'regional'
    ? `Região ${regiao || chave}`
    : `Unidade ${chave}`;

  return (
    <div className="dre-container">
      <div className="dre-header">
        <h2><span className="page-icon"><IconChart /></span>DRE — {escopo === 'regional' ? 'Regional' : 'Unidade'}</h2>
        <p>{subtitulo}</p>
      </div>

      <div className="dre-periodo">
        <label>
          Mês
          <select value={mes} onChange={e => setMes(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{nomeMes(m)}</option>
            ))}
          </select>
        </label>
        <label>
          Ano
          <select value={ano} onChange={e => setAno(Number(e.target.value))}>
            {anosDisponiveis.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="access-denied-card">
          <span className="access-icon"><IconAlert /></span>
          <h3>Não foi possível carregar</h3>
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <p className="loading-text">Carregando resultados...</p>
      ) : !error && (
        <>
          <DreIndicadores
            indicadores={indicadores}
            justificativas={justificativas}
            editable={true}
            onSaveJustificativa={handleSaveJustificativa}
          />

          <DrePlanoAcao planoAcao={planoAcao} editable={true} onSave={handleSavePlano} />

          {escopo === 'regional' && <DreUnidades ano={ano} mes={mes} />}
        </>
      )}
    </div>
  );
}

export default Dre;
